import type { InstrumentRunConsumables } from '@/types';
import { Grid } from '@nautilusbio/nb-design-system/Grid';
import { Input } from '@nautilusbio/nb-design-system/Input';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import clsx from 'clsx';
import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import parentStyles from '../../styles.module.css';

import styles from './styles.module.css';

type Props = {
  values: Partial<InstrumentRunConsumables> | null | undefined;
  isEditing: boolean;
  onChange?: (field: keyof InstrumentRunConsumables, value: string) => void;
};

type FieldProps = {
  label: string;
  field: keyof InstrumentRunConsumables;
  id?: string;
  value?: string | null;
  isEditing: boolean;
  alwaysDisabled?: boolean;
  onChange?: (field: keyof InstrumentRunConsumables, value: string) => void;
};

const ConsumableInput = ({
  label,
  field,
  id: idProp,
  value,
  isEditing,
  alwaysDisabled = false,
  onChange,
}: FieldProps) => {
  const disabled = alwaysDisabled || !isEditing;
  const inputId = idProp ?? `consumable-${field}`;
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(field, e.target.value);
    },
    [field, onChange],
  );

  return (
    <Input
      classNames={{
        label: parentStyles.consumableLabel,
        input: clsx(disabled && styles.disabledInput),
      }}
      containerClassName={clsx(!alwaysDisabled && disabled && styles.readableDisabled)}
      data-testid={inputId}
      disabled={disabled}
      id={inputId}
      label={{ children: label, id: `label-${inputId}`, htmlFor: inputId }}
      onChange={handleChange}
      placeholder={disabled ? undefined : label}
      size="sm"
      value={value ?? ''}
    />
  );
};

const InstrumentRunConsumables = ({ values: c, isEditing, onChange }: Props) => {
  const { t } = useTranslation('runs', { keyPrefix: 'header' });

  if (!c) return null;

  const flowCellIds = (c.three_flow_cell_ids ?? '').split(',').map(s => s.trim());
  const [fc1, fc2, fc3] = flowCellIds;

  return (
    <Stack className={parentStyles.consumablesContainer} fullWidth gap={3} orientation="vertical">
      <Text as="h3">{t('consumables')}</Text>

      <Grid className={parentStyles.consumablesGrid} gridTemplateColumns="repeat(4, 1fr)">
        {/* Row 1: Flowcell SNs — always disabled (instrument-controlled) */}
        <ConsumableInput
          alwaysDisabled
          field="three_flow_cell_ids"
          id="consumable-flow_cell_1"
          isEditing={isEditing}
          label={t('cons_flowcell_1')}
          value={fc1}
        />
        <ConsumableInput
          alwaysDisabled
          field="three_flow_cell_ids"
          id="consumable-flow_cell_2"
          isEditing={isEditing}
          label={t('cons_flowcell_2')}
          value={fc2}
        />
        <ConsumableInput
          alwaysDisabled
          field="three_flow_cell_ids"
          id="consumable-flow_cell_3"
          isEditing={isEditing}
          label={t('cons_flowcell_3')}
          value={fc3}
        />
        <div />

        {/* Row 2 */}
        <ConsumableInput
          field="sample_plate_id"
          isEditing={isEditing}
          label={t('cons_sample_plate')}
          onChange={onChange}
          value={c.sample_plate_id}
        />
        <ConsumableInput
          field="lobe_plate_id"
          isEditing={isEditing}
          label={t('cons_detection_plate')}
          onChange={onChange}
          value={c.lobe_plate_id}
        />
        <div />
        <div />

        {/* Row 3 */}
        <ConsumableInput
          field="brm_tray_id"
          isEditing={isEditing}
          label={t('cons_system_reagents')}
          onChange={onChange}
          value={c.brm_tray_id}
        />
        <ConsumableInput
          field="buffer_base_id"
          isEditing={isEditing}
          label={t('cons_run_buffer')}
          onChange={onChange}
          value={c.buffer_base_id}
        />
        <ConsumableInput
          field="sys_clean_kit_sn"
          isEditing={isEditing}
          label={t('cons_system_clean')}
          onChange={onChange}
          value={c.sys_clean_kit_sn}
        />
        <div />

        {/* Row 4 */}
        <ConsumableInput
          field="sys_reagent_kit_additive_a_sn"
          isEditing={isEditing}
          label={t('cons_additive_a')}
          onChange={onChange}
          value={c.sys_reagent_kit_additive_a_sn}
        />
        <ConsumableInput
          field="sys_reagent_kit_additive_b_sn"
          isEditing={isEditing}
          label={t('cons_additive_b')}
          onChange={onChange}
          value={c.sys_reagent_kit_additive_b_sn}
        />
        <ConsumableInput
          field="sys_reagent_kit_additive_c_sn"
          isEditing={isEditing}
          label={t('cons_additive_c')}
          onChange={onChange}
          value={c.sys_reagent_kit_additive_c_sn}
        />
        <div />

        {/* Row 5 */}
        <ConsumableInput
          field="library_prep_kit_plus_4c_lot_id"
          isEditing={isEditing}
          label={t('cons_lib_prep_a')}
          onChange={onChange}
          value={c.library_prep_kit_plus_4c_lot_id}
        />
        <ConsumableInput
          field="library_prep_kit_minus_20c_lot_id"
          isEditing={isEditing}
          label={t('cons_lib_prep_b')}
          onChange={onChange}
          value={c.library_prep_kit_minus_20c_lot_id}
        />
        <ConsumableInput
          field="detection_kit_controls_sn"
          isEditing={isEditing}
          label={t('cons_lib_prep_c')}
          onChange={onChange}
          value={c.detection_kit_controls_sn}
        />
        <ConsumableInput
          field="library_prep_kit_process_controls_lot_id"
          isEditing={isEditing}
          label={t('cons_lib_prep_controls')}
          onChange={onChange}
          value={c.library_prep_kit_process_controls_lot_id}
        />
      </Grid>
    </Stack>
  );
};

export default memo(InstrumentRunConsumables);
